import { useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileUp, Loader2 } from "lucide-react";
import { ConsentPill } from "@/components/t4r/learners/ConsentPill";

const DEFAULT_GROUP = "T4R Complied - Opt In List";
const TONE = { create: "bg-green-100 text-green-800", existing: "bg-sky-100 text-sky-800", error: "bg-red-100 text-red-800" };

const MappingTable = ({ mapping }) => (
  <div className="rounded-md border border-border" data-testid="eoi-mapping">
    <p className="border-b border-border bg-slate-50 px-2 py-1.5 text-[11px] font-semibold text-slate-600">Columns used. Everything else on the sheet is ignored and not saved.</p>
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 p-2 text-xs sm:grid-cols-4">
      {mapping.map((m) => (
        <div key={m.field} className="min-w-0" data-testid={`eoi-map-${m.field}`}>
          <p className="font-medium text-slate-800">{m.label}</p>
          <p className={m.column ? "text-muted-foreground" : "text-amber-700"}>{m.column ? `Column ${m.letter}: "${m.column}"` : "Not found"}</p>
        </div>
      ))}
    </div>
  </div>
);

const RowsTable = ({ rows }) => (
  <div className="max-h-[280px] overflow-auto rounded-md border border-border">
    <table className="w-full min-w-[560px] text-xs">
      <thead className="sticky top-0 bg-slate-50 text-left"><tr><th className="px-2 py-1.5">Row</th><th className="px-2 py-1.5">Person</th><th className="px-2 py-1.5">Dates</th><th className="px-2 py-1.5">Consent</th><th className="px-2 py-1.5">Result</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.line} className="border-t border-border align-top" data-testid={`eoi-row-${r.line}`}>
            <td className="px-2 py-1.5 text-muted-foreground">{r.line}</td>
            <td className="px-2 py-1.5"><p className="font-medium">{r.name || "—"}</p><p className="text-muted-foreground">{r.email || "—"}</p></td>
            <td className="px-2 py-1.5 text-muted-foreground"><p>Submitted {r.submission_date || "—"}</p><p>Signed {r.signature_date || "—"}{r.has_signature ? " (link)" : ""}</p></td>
            <td className="px-2 py-1.5"><ConsentPill consent={r.consent} /></td>
            <td className="px-2 py-1.5"><Badge variant="outline" className={`text-[10px] capitalize ${TONE[r.status]}`}>{r.status === "create" ? "new" : r.status}</Badge>{[...r.errors, ...r.warnings].map((m, i) => <p key={i} className={r.errors.includes(m) ? "text-red-700" : "text-muted-foreground"}>{m}</p>)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const EoiImportDialog = ({ open, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [group, setGroup] = useState(DEFAULT_GROUP);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const close = () => { setFile(null); setPreview(null); setGroup(DEFAULT_GROUP); onClose(); };

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/lms/admin/eoi/import?dry_run=${dryRun}&group_name=${encodeURIComponent(group)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (!dryRun) { toast.success(`Saved ${data.summary.create} new and ${data.summary.existing} existing people to "${data.group_name}"`); onImported(); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); }
  };
  const s = preview?.summary;
  const saving = s ? s.create + s.existing : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto grid-cols-[minmax(0,1fr)]" data-testid="eoi-import-dialog">
        <DialogHeader>
          <DialogTitle>Import EOI sheet</DialogTitle>
          <DialogDescription className="text-xs">Upload the Greenskills EOI export (.xlsx or .csv). People are saved in T4R only, as applicants. No invites or emails are sent. Nothing is saved until you confirm.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs">Email group</Label><Input value={group} onChange={(e) => { setGroup(e.target.value); setPreview(null); }} className="h-9 text-sm" data-testid="eoi-group-name" /></div>
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
              <label className="inline-flex h-9 min-w-0 flex-1 cursor-pointer items-center gap-1 truncate rounded-md border border-border px-3 text-xs hover:bg-slate-50">
                <FileUp size={12} /> <span className="truncate">{file ? file.name : "Choose sheet"}</span>
                <input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="eoi-file" />
              </label>
              <Button size="sm" variant="outline" className="h-9" disabled={!file || busy} onClick={() => run(true)} data-testid="eoi-preview-btn">{busy ? <Loader2 size={12} className="animate-spin" /> : "Preview plan"}</Button>
            </div>
          </div>
          {preview && <MappingTable mapping={preview.mapping} />}
          {s && (
            <div className="flex flex-wrap gap-1.5 text-xs" data-testid="eoi-summary">
              <span className="rounded-full bg-slate-100 px-2 py-0.5">{s.total} rows</span>
              <span className={`rounded-full px-2 py-0.5 ${TONE.create}`}>{s.create} new</span>
              <span className={`rounded-full px-2 py-0.5 ${TONE.existing}`}>{s.existing} already in T4R</span>
              <span className={`rounded-full px-2 py-0.5 ${TONE.error}`}>{s.error} skipped</span>
              <span className="rounded-full bg-green-50 px-2 py-0.5 text-green-800">{s.consent_yes} consented, will be emailed</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{s.consent_no} no consent, won't be emailed</span>
              {!preview.dry_run && <span className="font-medium text-green-700" data-testid="eoi-done">Import complete</span>}
            </div>
          )}
          {preview && <RowsTable rows={preview.rows} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Close</Button>
          <Button className="bg-green-700 text-white hover:bg-green-800" disabled={!preview?.dry_run || !saving || busy} onClick={() => run(false)} data-testid="eoi-commit-btn">Confirm and save {saving ? `(${saving})` : ""}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
